import { useEffect, useRef, useState, useId } from 'react';
import { Icon } from './Icon';
import { useScrollLock, useFocusTrap } from '../../lib/overlay';
import { useConfirmStore } from '../../stores/confirm.store';

/**
 * Root del sistema di conferma. Va montato UNA volta, sopra i modali (in AppShell).
 * Si pilota imperativamente con `confirm(...)` da qualsiasi punto dell'app.
 */
export function ConfirmRoot() {
  const open = useConfirmStore((s) => s.open);
  const opts = useConfirmStore((s) => s.opts);
  if (!open || !opts) return null;
  return <ConfirmDialog />;
}

function ConfirmDialog() {
  const opts = useConfirmStore((s) => s.opts)!;
  const closing = useConfirmStore((s) => s.closing);
  const settle = useConfirmStore((s) => s.settle);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [typed, setTyped] = useState('');

  useScrollLock(true);
  useFocusTrap(panelRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false);
      else if (e.key === 'Enter' && (!opts.requireType || typed === opts.requireType)) {
        e.preventDefault();
        settle(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settle, opts.requireType, typed]);

  const blocked = !!opts.requireType && typed !== opts.requireType;

  return (
    <div
      className={`modal-backdrop confirm-backdrop${closing ? ' modal-backdrop--leaving' : ''}`}
      onClick={(e) => e.target === e.currentTarget && settle(false)}
    >
      <div
        ref={panelRef}
        className={`modal modal-md confirm-modal${closing ? ' modal--leaving' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="confirm-bd">
          <div className={`confirm-icon ${opts.danger ? 'danger' : ''}`}>
            <Icon name={opts.icon ?? (opts.danger ? 'alert' : 'help')} size={18} />
          </div>
          <div className="t-h2" id={titleId}>{opts.title}</div>
          {opts.body && <div className="confirm-body-text">{opts.body}</div>}
          {opts.requireType && (
            <div className="confirm-type">
              <label className="field-label">
                Digita <span className="confirm-type-token">{opts.requireType}</span> per confermare
              </label>
              <input
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                spellCheck={false}
                autoComplete="off"
              />
            </div>
          )}
        </div>
        <div className="modal-ft confirm-ft">
          <button className="btn btn-ghost" onClick={() => settle(false)}>
            {opts.cancelLabel ?? 'Annulla'}
          </button>
          <button
            className={`btn ${opts.danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => settle(true)}
            disabled={blocked}
          >
            {opts.confirmLabel ?? (opts.danger ? 'Elimina' : 'Conferma')}
          </button>
        </div>
      </div>
    </div>
  );
}
